import React, { useEffect, useState, useMemo } from 'react';
import Portal from 'core/components/Modal/Portal';
import ModalClasses from 'core/components/Modal/modal.module.scss';
import { GetLegalAgreements, PostUserAgreement } from 'core/API/legalAgreements';
import parse from 'html-react-parser';
import { Button } from '@mui/material';
import { sanitizeHtml } from 'shared/utils/sanitizeHtml';
import styles from './LegalAgreementModal.module.scss';

interface ILegalAgreementModal {
	open: boolean;
	onClose: () => void;
	userId: string | number;
	activeIdentifier?: string | null;
}

const LegalAgreementModal: React.FC<ILegalAgreementModal> = ({
	open,
	onClose,
	userId,
	activeIdentifier,
}) => {
	const [agreementText, setAgreementText] = useState<string>('');
	const [identifier, setIdentifier] = useState<string>('');
	const [agreeChecked, setAgreeChecked] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!open) return;

		let mounted = true;
		const fetch = async () => {
			try {
				const items = await GetLegalAgreements();
				if (!mounted) return;
				if (items && items.length > 0) {
					let m = null as any;
					if (activeIdentifier) {
						m = items.find((it: any) => it.identifier === activeIdentifier) || null;
					}
					if (!m) m = items[0];

					setAgreementText(m?.text || '');
					setIdentifier(m?.identifier || '');
					setAgreeChecked(false);
				}
			} catch (err) {
				console.error('Failed to fetch legal agreements', err);
			}
		};

		fetch();
		return () => {
			mounted = false;
		};
	}, [open, activeIdentifier]);

	const parsedContent = useMemo(
		() => parse(sanitizeHtml(agreementText)),
		[agreementText]
	);

	const handleConfirm = async () => {
		setLoading(true);
		try {
			const payload = {
				legalAgreementIdentifier: identifier,
				deviceIdentifier: `web-${userId}`,
				geolocation: {},
			};

			await PostUserAgreement(payload);
			onClose();
		} catch (err) {
			console.error('Failed to post agreement', err);
		} finally {
			setLoading(false);
		}
	};

	if (!open) return null;

	return (
		<Portal>
			<div className={ModalClasses.modalCenterRoot}>
				<div className={ModalClasses.modalCenterWrapper}>
					<div className={ModalClasses.modalHeader}>
						<div className={ModalClasses.modalTitle}>
							<h4>End User License Agreement</h4>
						</div>
					</div>

					<div className={styles.root}>
						<div className={styles.content}>
							{parsedContent}
							<div className={styles.agreeText}>
								<input
									type="checkbox"
									id="agree"
									checked={agreeChecked}
									onChange={(e) => setAgreeChecked(e.target.checked)}
								/>
								<label htmlFor="agree">I have read and agree to the Legal Agreement.</label>
							</div>
						</div>

						<div className={styles.footer}>
							<Button
								variant="contained"
								className={styles.confirmBtn}
								onClick={handleConfirm}
								disabled={!agreeChecked || loading}
							>
								{loading ? 'Confirming...' : 'Confirm'}
							</Button>
						</div>
					</div>
				</div>
				<div className={ModalClasses.modalBackdrop} />
			</div>
		</Portal>
	);
};

export default LegalAgreementModal;
